import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ConfirmCsvUploadDialog from 'components/CsvUpload/ConfirmCsvUploadDialog';
import MapCsvHeaders, { getAttributesForBusinessType } from 'components/CsvUpload/MapCsvHeaders';
import Papa from 'papaparse';
import type React from 'react';
import { useEffect, useState } from 'react';
import type { BodyComponentInput, CampaignData } from 'services/api/adminCampaigns.api';
import { useMapCSVHeadersMutation } from 'services/api/adminCampaigns.api';
import { useGetBusinessUnitQuery } from 'services/api/businessUnits.api';
import { templateAttributeToCsvKey } from 'services/types/client';
import { NAVY, RADIUS_COIN, RADIUS_ITEM, SHADOW, SURFACE_CONTAINER_LOWEST, YELLOW } from 'styles/tokens';

interface ParseIssue {
  row?: number;
  code: string;
  message: string;
}

interface UploadCampaignFileProps {
  campaignData: CampaignData;
  setCampaignData: React.Dispatch<React.SetStateAction<CampaignData>>;
  onNext: () => void;
  onBack: () => void;
}

const UploadCampaignFile: React.FC<UploadCampaignFileProps> = ({ campaignData, setCampaignData, onNext, onBack }) => {
  const [fileToSend, setFileToSend] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [exampleData, setExampleData] = useState<string[][]>([]);
  const [mappedHeaders, setMappedHeaders] = useState<{ headerName: string; attrCliente: string }[]>([]);
  const [parseIssues, setParseIssues] = useState<ParseIssue[]>([]);
  const [fatalParseError, setFatalParseError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [mapCSVHeaders, { data: newMappedHeaders }] = useMapCSVHeadersMutation();
  const { data: businessUnitData } = useGetBusinessUnitQuery(campaignData.businessUnitId ?? 0, {
    skip: !campaignData.businessUnitId,
  });

  const hasPhoneAttr = mappedHeaders.find((h) => h.attrCliente === 'phone');

  const businessUnitType = businessUnitData?.type || 'car_dealership';
  const csvAttributeLabels = getAttributesForBusinessType(businessUnitType, campaignData.businessUnitId || undefined);

  // Attributes referenced by the selected template parameters must be present as CSV columns,
  // otherwise the template would be sent with empty/missing values for those placeholders.
  const templateCsvAttrs = (campaignData.templateBodyParameters ?? [])
    .map((component: BodyComponentInput) => (typeof component === 'string' ? component : component.value))
    // Attribute values may carry a `||default` suffix; the mapping key is the bare attribute.
    .map((value) => templateAttributeToCsvKey[value.split('||')[0]])
    .filter(Boolean)
    .flat();

  // brand and model are mandatory columns on the vehicle, so mapping any vehicle attribute without
  // them would silently create no ClientCar at all.
  const hasOwnedCarAttr = mappedHeaders.some((h) => h.attrCliente.startsWith('ownedCar'));
  const requiredCsvAttrs = [
    ...new Set([...templateCsvAttrs, ...(hasOwnedCarAttr ? ['ownedCarBrand', 'ownedCarModel'] : [])]),
  ];
  const missingRequiredAttrs = requiredCsvAttrs.filter((attr) => !mappedHeaders.some((h) => h.attrCliente === attr));

  // The vehicle is matched by VIN or license plate; a feed carries one or the other.
  const missingOwnedCarIdentifier =
    hasOwnedCarAttr &&
    !mappedHeaders.some((h) => h.attrCliente === 'ownedCarVin' || h.attrCliente === 'ownedCarLicensePlate');

  const parseFile = async (file: File) => {
    setFatalParseError(null);
    setParseIssues([]);
    const text = (await file.text()).replace(/^﻿/, '');

    const parsed = Papa.parse<string[]>(text, {
      header: false,
      skipEmptyLines: 'greedy',
      delimitersToGuess: [',', ';', '\t', '|'],
      transform: (value) => (typeof value === 'string' ? value.trim() : value),
    });

    const allRows = parsed.data.filter((row) => row.some((cell) => cell !== ''));
    if (allRows.length === 0) {
      setFatalParseError('El archivo no contiene filas.');
      setHeaders([]);
      setExampleData([]);
      setParseIssues([]);
      return;
    }

    const detectedDelimiter = parsed.meta.delimiter;
    if (!detectedDelimiter) {
      setFatalParseError('No se pudo detectar el separador del CSV. Verifica el archivo.');
      setHeaders([]);
      setExampleData([]);
      setParseIssues([]);
      return;
    }

    const newHeaders = allRows[0].map((h) => h.trim().replace(/^﻿/, ''));
    const newExampleData = allRows.slice(1, 6);
    const totalRows = allRows.length - 1;

    const issues: ParseIssue[] = parsed.errors.map((err) => ({
      row: typeof err.row === 'number' ? err.row + 1 : undefined,
      code: err.code,
      message: err.message,
    }));

    setFatalParseError(totalRows === 0 ? 'El archivo no contiene filas de datos debajo del encabezado.' : null);
    setHeaders(newHeaders);
    setExampleData(newExampleData);
    setParseIssues(issues);

    setCampaignData((prev) => ({
      ...prev,
      totalFileRows: totalRows,
      uploadedFile: file,
      separator: detectedDelimiter,
    }));

    mapCSVHeaders({
      headers: newHeaders,
      exampleData: newExampleData,
    }).catch((e) => {
      console.error(e);
    });
  };

  const handleConfirmUpload = () => {
    setConfirmOpen(false);
    onNext();
  };

  useEffect(() => {
    if (fileToSend) {
      parseFile(fileToSend).catch((e) => {
        console.error(e);
      });
    }
  }, [fileToSend]);

  // The AI is offered every attribute regardless of business unit, so drop anything this business
  // unit does not expose — an unlisted attribute leaves the Select with an out-of-range value and
  // would persist data the operator was never shown.
  useEffect(() => {
    if (newMappedHeaders?.mappedHeaders) {
      const allowedAttrs = new Set(
        Object.keys(getAttributesForBusinessType(businessUnitType, campaignData.businessUnitId || undefined))
      );
      setMappedHeaders(newMappedHeaders.mappedHeaders.filter((h) => allowedAttrs.has(h.attrCliente)));
    }
  }, [newMappedHeaders, businessUnitType, campaignData.businessUnitId]);

  // Keep the submitted mappedHeaders in sync with the local edits, so manual changes in
  // MapCsvHeaders override the AI auto-mapping instead of only the initial AI result reaching
  // campaignData.
  useEffect(() => {
    setCampaignData((prev) => ({ ...prev, mappedHeaders }));
  }, [mappedHeaders, setCampaignData]);

  const handleNext = () => {
    if (!hasPhoneAttr || !fileToSend || fatalParseError || missingRequiredAttrs.length > 0 || missingOwnedCarIdentifier)
      return;
    setConfirmOpen(true);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 1000, mx: 'auto' }}>
      {/* File upload card */}
      <Box>
        <Typography variant="h6" sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, color: NAVY, mb: 0.5 }}>
          Archivo de Clientes
        </Typography>
        <Typography variant="body2" sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, opacity: 0.55, mb: 3 }}>
          Sube un archivo CSV con los datos de los clientes. Debe incluir una columna con números de teléfono.
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <label htmlFor="file-input">
            <input
              id="file-input"
              name="file-input"
              type="file"
              style={{ display: 'none' }}
              accept=".csv"
              onChange={(e) => {
                if (e.target.files) setFileToSend(e.target.files[0]);
              }}
            />
            <Button
              component="span"
              startIcon={<AttachFileIcon />}
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontWeight: 600,
                color: NAVY,
                bgcolor: 'rgba(21, 24, 58, 0.07)',
                borderRadius: RADIUS_COIN,
                px: 2.5,
                '&:hover': { bgcolor: 'rgba(21, 24, 58, 0.12)' },
              }}
            >
              Seleccionar CSV
            </Button>
          </label>

          {fileToSend && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: SURFACE_CONTAINER_LOWEST,
                borderRadius: RADIUS_ITEM,
                px: 2,
                py: 0.75,
                boxShadow: SHADOW,
              }}
            >
              <InsertDriveFileIcon sx={{ fontSize: 16, color: NAVY, opacity: 0.5 }} />
              <Typography variant="body2" sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, fontWeight: 500 }}>
                {fileToSend.name}
              </Typography>
              {campaignData.totalFileRows != null && (
                <Typography variant="caption" sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, opacity: 0.45 }}>
                  ({campaignData.totalFileRows} filas)
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Parse issues */}
      {(fatalParseError || parseIssues.length > 0) && fileToSend && (
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: 'flex-start',
            bgcolor: fatalParseError ? 'rgba(176, 0, 32, 0.07)' : 'rgba(207, 92, 9, 0.08)',
            color: fatalParseError ? '#B00020' : '#CF5C09',
            px: 2,
            py: 1.5,
            borderRadius: RADIUS_ITEM,
          }}
        >
          <ReportProblemRoundedIcon fontSize="small" sx={{ mt: 0.25 }} />
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontWeight: 700,
                fontSize: '0.95rem',
                color: fatalParseError ? '#B00020' : NAVY,
                mb: 0.5,
              }}
            >
              {fatalParseError ? fatalParseError : `${parseIssues.length} problema(s) al leer el CSV`}
            </Typography>
            {parseIssues.length > 0 && (
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {parseIssues.slice(0, 8).map((issue, idx) => (
                  <Box
                    component="li"
                    key={`${issue.code}-${issue.row ?? 'na'}-${idx}`}
                    sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.85rem', color: NAVY }}
                  >
                    {issue.row ? `Fila ${issue.row}: ` : ''}
                    {issue.message} ({issue.code})
                  </Box>
                ))}
                {parseIssues.length > 8 && (
                  <Box
                    component="li"
                    sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.85rem', color: NAVY, opacity: 0.6 }}
                  >
                    +{parseIssues.length - 8} más…
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* CSV preview */}
      {headers.length > 0 && exampleData.length > 0 && fileToSend && (
        <Box>
          <Typography variant="h6" sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, color: NAVY, mb: 2 }}>
            Vista previa — {fileToSend.name}
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Box
              component="table"
              sx={{
                width: '100%',
                borderCollapse: 'collapse',
                fontFamily: 'Jost, sans-serif',
              }}
            >
              <Box component="thead">
                <Box component="tr">
                  {headers.map((header) => (
                    <Box
                      component="th"
                      key={header}
                      title={header}
                      sx={{
                        px: 2,
                        py: 1,
                        bgcolor: 'rgba(21, 24, 58, 0.06)',
                        color: NAVY,
                        fontFamily: 'Jost, sans-serif',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        textAlign: 'left',
                        borderRadius: 0,
                        maxWidth: 200,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {header}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {exampleData.map((row, rowIdx) => (
                  <Box
                    component="tr"
                    key={row.join('')}
                    sx={{
                      bgcolor: rowIdx % 2 === 0 ? SURFACE_CONTAINER_LOWEST : 'transparent',
                      '&:hover': { bgcolor: 'rgba(21, 24, 58, 0.03)' },
                    }}
                  >
                    {row.map((cell, cellIdx) => (
                      <Box
                        component="td"
                        key={`${cell}-${cellIdx}`}
                        title={cell}
                        sx={{
                          px: 2,
                          py: 1,
                          color: NAVY,
                          fontFamily: 'Jost, sans-serif',
                          fontSize: '0.875rem',
                          opacity: 0.75,
                          maxWidth: 200,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {cell}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Header mapping */}
      {headers.length > 0 && exampleData.length > 0 && (
        <MapCsvHeaders
          rows={headers.map((header, index) => ({
            headerName: header.trim(),
            examples: exampleData.map((row) => row[index]?.trim() || ''),
          }))}
          mappedHeaders={mappedHeaders}
          setMappedHeaders={setMappedHeaders}
          businessUnitType={businessUnitData?.type || 'car_dealership'}
          businessUnitId={campaignData.businessUnitId || undefined}
        />
      )}

      {!hasPhoneAttr && headers.length > 0 && (
        <Typography variant="body2" sx={{ fontFamily: 'Jost, sans-serif', color: '#B00020' }}>
          * No se ha seleccionado el atributo de teléfono. Este campo es obligatorio para enviar la campaña.
        </Typography>
      )}

      {missingRequiredAttrs.length > 0 && headers.length > 0 && (
        <Typography variant="body2" sx={{ fontFamily: 'Jost, sans-serif', color: '#B00020' }}>
          * Los parámetros de la plantilla requieren mapear:{' '}
          {missingRequiredAttrs.map((attr) => csvAttributeLabels[attr] || attr).join(', ')}.
        </Typography>
      )}

      {missingOwnedCarIdentifier && headers.length > 0 && (
        <Typography variant="body2" sx={{ fontFamily: 'Jost, sans-serif', color: '#B00020' }}>
          * Para cargar el vehículo debes mapear el VIN o la patente.
        </Typography>
      )}

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button
          onClick={onBack}
          sx={{
            fontFamily: 'Jost, sans-serif',
            fontWeight: 600,
            color: NAVY,
            bgcolor: 'rgba(21, 24, 58, 0.07)',
            borderRadius: RADIUS_COIN,
            px: 2.5,
            '&:hover': { bgcolor: 'rgba(21, 24, 58, 0.12)' },
          }}
        >
          Atrás
        </Button>
        <Button
          onClick={handleNext}
          disabled={
            !hasPhoneAttr ||
            !fileToSend ||
            !!fatalParseError ||
            missingRequiredAttrs.length > 0 ||
            missingOwnedCarIdentifier
          }
          sx={{
            bgcolor: YELLOW,
            color: NAVY,
            fontFamily: 'Jost, sans-serif',
            fontWeight: 700,
            borderRadius: RADIUS_COIN,
            px: 4,
            boxShadow: SHADOW,
            '&:hover': { bgcolor: '#e6c009' },
            '&:disabled': { bgcolor: 'rgba(21,24,58,0.1)', color: 'rgba(21,24,58,0.3)' },
          }}
        >
          Siguiente
        </Button>
      </Box>

      <ConfirmCsvUploadDialog
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirmUpload}
        fileName={fileToSend?.name ?? ''}
        totalRows={campaignData.totalFileRows ?? 0}
        parseIssuesCount={parseIssues.length}
        mappedHeaders={mappedHeaders}
        businessUnitType={businessUnitData?.type || 'car_dealership'}
        businessUnitId={campaignData.businessUnitId || undefined}
      />
    </Box>
  );
};

export default UploadCampaignFile;
